import { motion } from 'framer-motion';

/**
 * Stylised Android home-screen mockup for the Vpnee app. Pure SVG + CSS —
 * no live data, no raster. Creative "radar" connect hub: concentric rings,
 * a rotating conic sweep, and a glossy power button that mirrors the real
 * client's aesthetic. Scales cleanly and stays tiny on the wire.
 */
export default function PhoneMock({ state = 'connected' }: { state?: 'connected' | 'idle' }) {
  const isOn = state === 'connected';
  return (
    <div className="relative w-full max-w-[280px] aspect-[280/580] mx-auto select-none">
      {/* Device frame */}
      <div className="absolute inset-0 rounded-[44px] bg-gradient-to-b from-[#20121a] via-[#12080d] to-[#080509] shadow-[0_50px_130px_rgba(232,71,106,0.28),inset_0_1px_0_rgba(255,255,255,0.08)] border border-[color:var(--color-line-2)]" />
      <div className="absolute inset-[3px] rounded-[42px] border border-black/60" />

      {/* Screen */}
      <div className="absolute inset-[7px] rounded-[37px] overflow-hidden bg-[#0a0508]">
        {/* faint grid + ambient glow */}
        <div className="absolute inset-0 opacity-[0.5] pointer-events-none grid-bg" />
        <div className="absolute -top-20 left-1/2 -translate-x-1/2 w-64 h-64 rounded-full bg-[color:var(--color-brand)]/25 blur-3xl pointer-events-none" />

        {/* Dynamic-island */}
        <div className="absolute top-2 left-1/2 -translate-x-1/2 w-20 h-6 rounded-full bg-black z-20 flex items-center justify-end pr-2">
          <span className="w-1.5 h-1.5 rounded-full bg-[color:var(--color-brand)]/70" />
        </div>

        {/* Status bar */}
        <div className="relative pt-3.5 px-5 flex justify-between items-center text-[10px] font-semibold text-[color:var(--color-ink-dim)] z-10">
          <span>9:41</span>
          <span className="flex items-center gap-1.5">
            {/* signal bars */}
            <span className="flex items-end gap-[2px] h-2.5">
              {[4, 6, 8, 10].map((h, i) => (
                <span key={i} className="w-[2px] rounded-sm bg-[color:var(--color-ink-dim)]" style={{ height: h }} />
              ))}
            </span>
            <svg width="12" height="10" viewBox="0 0 24 20" fill="currentColor" aria-hidden><path d="M12 3C7 3 3 6 1 9l11 11L23 9c-2-3-6-6-11-6z" opacity="0.85"/></svg>
            <span className="w-4 h-2 rounded-[3px] border border-[color:var(--color-ink-dim)] relative">
              <span className="absolute inset-[1px] rounded-[1px] bg-[color:var(--color-ok)]" style={{ width: '78%' }} />
            </span>
          </span>
        </div>

        {/* Top bar */}
        <div className="relative mt-5 px-4 flex items-center gap-2 z-10">
          <div className="w-9 h-9 rounded-xl bg-white/5 border border-[color:var(--color-line)] grid place-items-center text-[color:var(--color-ink-dim)]">
            <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2"><path d="M4 7h16M4 12h16M4 17h16" /></svg>
          </div>
          <div className="flex-1 h-9 rounded-xl bg-white/5 border border-[color:var(--color-line)] flex items-center gap-2 px-3 text-[11px] font-mono">
            <span className={`w-1.5 h-1.5 rounded-full ${isOn ? 'bg-[color:var(--color-ok)] animate-pulse' : 'bg-[color:var(--color-ink-faint)]'}`} />
            AUTO · BEST NODE
          </div>
          <div className="w-9 h-9 rounded-xl bg-white/5 border border-[color:var(--color-line)] grid place-items-center text-[color:var(--color-ink-faint)]">
            <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 2v6h-6M3 12a9 9 0 0 1 15-6.7L21 8M3 22v-6h6M21 12a9 9 0 0 1-15 6.7L3 16"/></svg>
          </div>
        </div>

        {/* Connect radar core */}
        <div className="relative mt-7 grid place-items-center">
          <div className="relative w-56 h-56 grid place-items-center">
            {/* concentric radar rings */}
            {[224, 188, 152].map((d, i) => (
              <span key={d} className="absolute rounded-full border" style={{
                width: d, height: d,
                borderColor: `rgba(232,71,106,${0.10 + i * 0.06})`,
              }} />
            ))}
            {/* rotating conic sweep (connected only) */}
            {isOn && (
              <motion.div
                className="absolute w-[200px] h-[200px] rounded-full"
                style={{ background: 'conic-gradient(from 0deg, transparent 0deg, rgba(232,71,106,0.55) 40deg, transparent 90deg)', WebkitMask: 'radial-gradient(farthest-side, transparent calc(100% - 3px), #000 calc(100% - 2px))', mask: 'radial-gradient(farthest-side, transparent calc(100% - 3px), #000 calc(100% - 2px))' }}
                animate={{ rotate: 360 }}
                transition={{ duration: 6, repeat: Infinity, ease: 'linear' }}
              />
            )}
            {/* expanding pulse rings */}
            {[0, 1, 2].map((i) => (
              <motion.div
                key={i}
                initial={{ scale: 0.7, opacity: 0.6 }}
                animate={isOn ? { scale: [0.7, 1.15, 0.7], opacity: [0.6, 0, 0.6] } : {}}
                transition={{ duration: 3, repeat: Infinity, delay: i * 0.9, ease: 'easeInOut' }}
                className="absolute w-[152px] h-[152px] rounded-full border border-[color:var(--color-brand)]/40"
              />
            ))}
            {/* glow behind button */}
            <div className={`absolute w-[150px] h-[150px] rounded-full blur-2xl ${isOn ? 'bg-[color:var(--color-brand)]/45' : 'bg-white/5'}`} />
            {/* button */}
            <motion.div
              whileTap={{ scale: 0.95 }}
              className={`relative w-[136px] h-[136px] rounded-full grid place-items-center text-white ${
                isOn
                  ? 'bg-[radial-gradient(circle_at_50%_30%,#ff6c86,var(--color-brand)_45%,var(--color-brand-deep))] shadow-[0_0_50px_rgba(232,71,106,0.65),inset_0_2px_10px_rgba(255,255,255,0.35)]'
                  : 'bg-[radial-gradient(circle_at_50%_30%,rgba(255,255,255,0.06),rgba(255,255,255,0.015))] border border-[color:var(--color-line-2)] shadow-[inset_0_2px_18px_rgba(0,0,0,0.5)]'
              }`}
            >
              <div className="text-center">
                <svg viewBox="0 0 24 24" width="34" height="34" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" className="mx-auto drop-shadow">
                  <path d="M18.36 6.64a9 9 0 1 1-12.73 0M12 2v10" />
                </svg>
                <div className="mt-1 text-[11px] font-extrabold tracking-widest uppercase">{isOn ? 'Connected' : 'Connect'}</div>
              </div>
            </motion.div>
          </div>
          <div className="mt-4 px-4 py-1.5 rounded-full bg-black/40 border border-[color:var(--color-line)] flex items-center gap-2 text-[11px] font-mono">
            <span className={`w-1.5 h-1.5 rounded-full ${isOn ? 'bg-[color:var(--color-ok)] animate-pulse' : 'bg-[color:var(--color-ink-faint)]'}`} />
            <span className={isOn ? 'text-[color:var(--color-ok)]' : 'text-[color:var(--color-ink-faint)]'}>
              {isOn ? 'PROTECTED · 00:14:22' : 'DISCONNECTED · --:--'}
            </span>
          </div>
        </div>

        {/* Stat row */}
        <div className="relative mt-5 px-4 grid grid-cols-3 gap-2 z-10">
          {[
            { l: 'Server', v: 'JP-1' },
            { l: 'Exit IP', v: isOn ? '138.2.·' : '—' },
            { l: 'Used', v: '4.2GB' },
          ].map((s) => (
            <div key={s.l} className="glass p-2.5 rounded-xl text-center">
              <div className="text-[11px] font-bold">{s.v}</div>
              <div className="text-[9px] uppercase tracking-widest text-[color:var(--color-ink-faint)] mt-0.5">{s.l}</div>
            </div>
          ))}
        </div>

        {/* Data bar */}
        <div className="relative mt-3 mx-4 p-3 rounded-xl bg-white/[0.03] border border-[color:var(--color-line)] z-10">
          <div className="flex justify-between items-baseline">
            <span className="text-[10px] text-[color:var(--color-ink-dim)]">Data Usage</span>
            <span className="chip !text-[9px] !py-0.5 !px-2">Gold</span>
          </div>
          <div className="mt-2 h-1.5 rounded-full bg-white/5 overflow-hidden">
            <motion.div className="h-full bg-gradient-to-r from-[color:var(--color-ok)] to-[color:var(--color-brand)]" initial={{ width: 0 }} animate={{ width: '38%' }} transition={{ duration: 1.1, ease: 'easeOut' }} />
          </div>
          <div className="mt-1 text-[10px] font-mono">4.2 GB <span className="text-[color:var(--color-ink-faint)]">/ 50 GB</span></div>
        </div>

        {/* Tabbar */}
        <div className="absolute bottom-2 left-2 right-2 h-14 rounded-2xl bg-black/40 border border-[color:var(--color-line)] backdrop-blur flex items-center justify-around px-2 z-10">
          {[
            { on: true, d: 'M18.36 6.64a9 9 0 1 1-12.73 0M12 2v10' },
            { on: false, d: 'M4 6h16M4 12h16M4 18h16' },
            { on: false, d: 'M12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6zM19 12a7 7 0 0 0-.1-1l2-1.6-2-3.4-2.4 1a7 7 0 0 0-1.7-1L16 3H8l-.8 2a7 7 0 0 0-1.7 1l-2.4-1-2 3.4L3 11a7 7 0 0 0 0 2l-2 1.6 2 3.4 2.4-1a7 7 0 0 0 1.7 1L8 21h8l.8-2.4' },
          ].map((t, i) => (
            <div key={i} className={`flex-1 h-11 rounded-xl grid place-items-center ${t.on ? 'bg-[color:var(--color-brand)]/15 text-[color:var(--color-brand-glow)]' : 'text-[color:var(--color-ink-faint)]'}`}>
              <svg viewBox="0 0 24 24" width="17" height="17" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d={t.d} /></svg>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
